import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  exportRemindersMd,
  exportRemindersXlsx,
  fetchReminderCalendar,
  getApiErrorMessage,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";
import Loading from "../components/Loading";
import { moscowToday } from "./vacationDates";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const MONTH_NAMES = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const FILTERS = [
  { id: "all", label: "Все" },
  { id: "salary", label: "ЗП" },
  { id: "advance", label: "Аванс" },
  { id: "text", label: "Тексты" },
];

const TEXT_KINDS = new Set(["monthly", "after_payroll", "after_advance"]);

function pad(value) {
  return String(value).padStart(2, "0");
}

function isoFromDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function moscowMonth(today = moscowToday()) {
  const [year, month] = today.split("-").map(Number);
  return { year, month };
}

function shiftMonth(year, month, delta) {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

function monthCells(year, month) {
  const lead = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const count = new Date(year, month, 0).getDate();
  const cells = [];
  for (let offset = -lead; offset < count; offset += 1) {
    const date = new Date(year, month - 1, 1 + offset);
    cells.push({ date: isoFromDate(date), outside: offset < 0 });
  }
  let last = cells[cells.length - 1].date.split("-").map(Number);
  while (cells.length % 7 !== 0) {
    const date = new Date(last[0], last[1] - 1, last[2] + 1);
    cells.push({ date: isoFromDate(date), outside: true });
    last = cells[cells.length - 1].date.split("-").map(Number);
  }
  return cells;
}

function matchesFilter(item, filter) {
  if (filter === "salary") return item.kind === "salary";
  if (filter === "advance") return item.kind === "advance";
  if (filter === "text") return TEXT_KINDS.has(item.kind);
  return true;
}

function chipMark(item) {
  if (item.kind === "salary") return "ЗП";
  if (item.kind === "advance") return "Аванс";
  return item.text;
}

function chipTitle(item) {
  return `${item.client_name} — ${chipMark(item)}`;
}

function chipClassName(item) {
  const kindClass = item.kind === "salary"
    ? "crc-chip-salary"
    : item.kind === "advance"
      ? "crc-chip-advance"
      : "crc-chip-text";
  return ["crc-chip", kindClass, item.is_note ? "crc-chip-note" : ""].filter(Boolean).join(" ");
}

function chipKey(item) {
  return `${item.client_id}-${item.kind}-${item.reminder_id ?? "payroll"}-${item.text}`;
}

export default function ClientReminderCalendar() {
  const today = moscowToday();
  const initial = moscowMonth(today);
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);
  const [filter, setFilter] = useState("all");
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchReminderCalendar(year, month)
      .then((data) => {
        if (cancelled) return;
        setDays(Array.isArray(data?.days) ? data.days : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setDays([]);
        setError(getApiErrorMessage(err, "Не удалось загрузить напоминания"));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, month, reloadKey]);

  const itemsByDate = new Map(
    days.map((day) => [String(day.date).slice(0, 10), Array.isArray(day.items) ? day.items : []])
  );
  const cells = monthCells(year, month);

  const goToday = () => {
    const next = moscowMonth();
    setYear(next.year);
    setMonth(next.month);
  };

  const moveMonth = (delta) => {
    const next = shiftMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
  };

  const downloadReminders = async (format) => {
    setExporting(true);
    setExportError("");
    const stamp = moscowToday();
    try {
      if (format === "xlsx") {
        await exportRemindersXlsx(`reminders_${stamp}.xlsx`);
      } else {
        await exportRemindersMd(`reminders_${stamp}.md`);
      }
    } catch {
      setExportError("Не удалось скачать");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-container crc-page">
      <header className="page-header">
        <h2>Напоминания</h2>
      </header>
      <div className="crc-toolbar">
        <div className="crc-nav">
          <button type="button" className="btn btn-sm" aria-label="Предыдущий месяц" onClick={() => moveMonth(-1)}>
            ‹
          </button>
          <span className="crc-month">{`${MONTH_NAMES[month - 1]} ${year}`}</span>
          <button type="button" className="btn btn-sm" aria-label="Следующий месяц" onClick={() => moveMonth(1)}>
            ›
          </button>
          <button type="button" className="btn btn-sm" onClick={goToday}>
            Сегодня
          </button>
        </div>
        <div className="crc-export">
          <button
            type="button"
            className="btn btn-sm"
            disabled={exporting}
            onClick={() => downloadReminders("xlsx")}
          >
            Excel
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={exporting}
            onClick={() => downloadReminders("md")}
          >
            Markdown
          </button>
          {exportError ? <span>{exportError}</span> : null}
        </div>
        <div className="crc-filters">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className="btn btn-sm"
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorRetry message={error} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : (
        <>
          {days.length === 0 && <p className="crc-empty">Нет напоминаний в этом месяце</p>}
          <div className="crc-grid">
            {WEEKDAYS.map((name) => (
              <div key={name} className="crc-weekday">{name}</div>
            ))}
            {cells.map((cell) => {
              const items = cell.outside ? [] : (itemsByDate.get(cell.date) || []).filter((item) => matchesFilter(item, filter));
              const className = [
                "crc-day",
                cell.outside ? "crc-day-outside" : "",
                !cell.outside && cell.date === today ? "crc-day-today" : "",
              ].filter(Boolean).join(" ");
              return (
                <div key={cell.date} className={className} data-date={cell.date}>
                  <span className="crc-day-num">{Number(cell.date.slice(8))}</span>
                  {items.map((item) => (
                    <Link
                      key={chipKey(item)}
                      className={chipClassName(item)}
                      to={`/client-directory?card=${item.client_id}`}
                      title={chipTitle(item)}
                    >
                      <span className="crc-chip-name">{item.client_name}</span>
                      {" "}
                      <span>{chipMark(item)}</span>
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
